import { ZoneTextLocation } from "@omnia/shared/layers/types"
import { NumericValuesViewer } from "@omnia/shared/layers/viewers/numeric"
import { IDatalayerMapInstance, INumericValuesViewerOptions, ISeedLayerViewer } from "../exports"
import { SeedRateUnit } from "../generated/enums"
import { getSeedRateUnitInfo, SeedRateUnitInfo } from "../units"
import { SeedsDatalayer } from "./seedsDatalayer"
import { SeedsDatalayerZone } from "./seedsDatalayerZone"
import { NumericRangeColorProvider } from "@omnia/shared/color/provider"

const colors = [
    [255, 164, 72],
    [221, 137, 52],
    [188, 112, 37],
    [155, 89, 21],
    [121, 67, 11],
    [88, 46, 4],
    [55, 28, 0]
]

const defaultUnit = getSeedRateUnitInfo(SeedRateUnit.Kg)

export class SeedLayerViewer extends NumericValuesViewer implements ISeedLayerViewer {

    static createViewer(layer: IDatalayerMapInstance) {

        const options: INumericValuesViewerOptions = {
            colorProvider: new NumericRangeColorProvider(colors)
        }

        return new SeedLayerViewer(layer, options)

    }

    /** Unit used to display rates */
    unit: "plan" | "seeds/m2" | "seeds/ha" = "plan"

    private planUnit: SeedRateUnitInfo

    constructor(
        layer: IDatalayerMapInstance,
        options: INumericValuesViewerOptions,
        private readonly seedRateLayer = layer instanceof SeedsDatalayer ? layer : undefined
    ) {
        super(layer, options)
    }

    refresh() {
        this.planUnit = this.seedRateLayer?.planUnit ?? defaultUnit
        super.refresh()
    }

    setupMinMaxZoneValues() {

        super.setupMinMaxZoneValues()

        if (this._layer instanceof SeedsDatalayer) {
            this._minZoneValue = this._layer.planMinRate
            this._maxZoneValue = this._layer.planMaxRate
        }

    }

    getKey() {

        const key = super.getKey()

        const { seedRateLayer: layer } = this

        if (layer) {

            const { unit } = getKeyEntry(0, this.seedRateLayer, this.unit, this.planUnit)

            return {
                ...key,
                id: `seed-rates-plan-${layer.planId}`,
                name: "Seed rates - " + layer.planName,
                unit
            }

        }

        return key

    }

    protected getKeyEntry(value: any, color: string) {

        const e = getKeyEntry(value, this._layer, this.unit, this.planUnit)

        return {
            value: e.value,
            text: e.text,
            color
        }

    }

    getValueText(value: number, location: ZoneTextLocation): string {
        const vt = getKeyEntry(value, this._layer, this.unit, this.planUnit)
        return vt.text + " " + vt.unit
    }

}

function getKeyEntry(value: number, layer: IDatalayerMapInstance, unit: ISeedLayerViewer["unit"], planUnit: SeedRateUnitInfo) {

    if (planUnit.unit !== SeedRateUnit.Kg) {

        return { value, text: (value || "").toString(), unit: planUnit.labels.rate }

    }

    if ((layer instanceof SeedsDatalayer) && layer.supportsSeedRatesConversion() && (unit === "plan")) {

        value = layer.convertSeedRatesToKg(value)

        return { value, text: value.toString(), unit: "kg/ha" }

    }

    if (unit === "seeds/ha") {
        value = value ? (value * 10000) : value
    }

    return { value, text: (value || "").toString(), unit: formatUnit(unit) }

}

function formatUnit(unit: ISeedLayerViewer["unit"]) {
    return unit.replace("2", "²")
}
